import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

export type ScoreSavedEvent = {
  eventId: number;
  judgeId: number;
  registeredPageantContestantId: number;
  pageantAvailableEventId: number;
  aocScoreCategoryId: number;
  score: number;
  notes?: string;
  savedAt: string;
};

export type JudgeStatusChangedEvent = {
  eventId: number;
  judgeId: number;
  status: 'Active' | 'Inactive';
  liveStatus?: 'Online' | 'Offline' | 'Scoring' | 'LoggedIn';
  lastSeenAt: string;
};

export type JudgePortalDataChangedEvent = {
  eventId: number;
  changeType:
    | 'RegisteredContestantAocChanged'
    | 'JudgeAocChanged'
    | 'JudgeStatusChanged';
  judgeId?: number;
  registeredContestantId?: number;
  pageantAvailableEventId?: number;
  status?: string;
  updatedAt: string;
};

export type ScoreDeletedEvent = {
  eventId: number;
  judgeId: number;
  judgeLabel?: string;
  judgeName?: string;

  registeredPageantContestantId: number;
  contestantNumber?: number | string;
  contestantName: string;
   notes?: string;

  pageantAvailableEventId: number;
  aocName: string;

  deletedScoreIds?: number[];
  deletedAt: string;
};

export type JudgeHelpMessageSentEvent = {
  eventId: number;
  judgeId: number;
  judgeLabel?: string;
  judgeName?: string;
  eventName?: string;
  message: string;
  sentAt: string;
};

type UseLiveScoresArgs = {
  eventId: number;
  enabled: boolean;
  onScoreSaved: (score: ScoreSavedEvent) => void;
    onScoreDeleted?: (score: ScoreDeletedEvent) => void;
  onJudgeStatusChanged?: (status: JudgeStatusChangedEvent) => void;
  onJudgePortalDataChanged?: (change: JudgePortalDataChangedEvent) => void;
  onJudgeHelpMessageSent?: (message: JudgeHelpMessageSentEvent) => void;
};



export function useLiveScores({
  eventId,
  enabled,
  onScoreSaved,
  onScoreDeleted,
  onJudgeStatusChanged,
  onJudgePortalDataChanged,
  onJudgeHelpMessageSent,
}: UseLiveScoresArgs): void {
  const onScoreSavedRef = useRef(onScoreSaved);
  const onJudgeStatusChangedRef = useRef(onJudgeStatusChanged);
  const onJudgePortalDataChangedRef = useRef(onJudgePortalDataChanged);
  const onScoreDeletedRef = useRef(onScoreDeleted);
const onJudgeHelpMessageSentRef = useRef(onJudgeHelpMessageSent);

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const isStartingRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);

  useEffect(() => {
    onScoreSavedRef.current = onScoreSaved;
  }, [onScoreSaved]);

  useEffect(() => {
  onScoreDeletedRef.current = onScoreDeleted;
}, [onScoreDeleted]);

useEffect(() => {
  onJudgeHelpMessageSentRef.current = onJudgeHelpMessageSent;
}, [onJudgeHelpMessageSent]);

  useEffect(() => {
    onJudgeStatusChangedRef.current = onJudgeStatusChanged;
  }, [onJudgeStatusChanged]);

  useEffect(() => {
    onJudgePortalDataChangedRef.current = onJudgePortalDataChanged;
  }, [onJudgePortalDataChanged]);

  useEffect(() => {
    if (!enabled || !eventId) return;

    let isMounted = true;

    const clearRetryTimer = () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const buildConnection = (): signalR.HubConnection => {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl('/hubs/event-live')
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      connection.on('ScoreSaved', (score: ScoreSavedEvent) => {
        console.log('[LiveScores] ScoreSaved received', score);
        onScoreSavedRef.current(score);
      });

      connection.on('JudgeStatusChanged', (status: JudgeStatusChangedEvent) => {
        console.log('[LiveScores] JudgeStatusChanged received', status);
        onJudgeStatusChangedRef.current?.(status);
      });

      connection.on('JudgeHelpMessageSent', (message: JudgeHelpMessageSentEvent) => {
  console.log('[LiveScores] JudgeHelpMessageSent received', message);
  onJudgeHelpMessageSentRef.current?.(message);
});

      connection.on('ScoreDeleted', (score: ScoreDeletedEvent) => {
  console.log('[LiveScores] ScoreDeleted received', score);
  onScoreDeletedRef.current?.(score);
});


      connection.on(
        'JudgePortalDataChanged',
        (change: JudgePortalDataChangedEvent) => {
          console.log('[LiveScores] JudgePortalDataChanged received', change);
          onJudgePortalDataChangedRef.current?.(change);
        },
      );

      connection.onreconnecting((err) => {
        console.warn('[LiveScores] reconnecting...', err);
      });

      connection.onreconnected(async () => {
        console.log('[LiveScores] reconnected');

        try {
          await connection.invoke('JoinEventGroup', eventId);
          console.log('[LiveScores] rejoined group', `event-${eventId}`);
        } catch (err) {
          console.warn('[LiveScores] failed to rejoin group', err);
        }
      });

      connection.onclose((err) => {
        console.warn('[LiveScores] closed', err);
      });

      return connection;
    };

    const startConnection = async () => {
      if (!isMounted) return;
      if (!navigator.onLine) return;
      if (isStartingRef.current) return;

      let connection = connectionRef.current;

      if (
        connection &&
        (connection.state === signalR.HubConnectionState.Connected ||
          connection.state === signalR.HubConnectionState.Connecting ||
          connection.state === signalR.HubConnectionState.Reconnecting)
      ) {
        return;
      }

      isStartingRef.current = true;

      try {
        if (!connection) {
          connection = buildConnection();
          connectionRef.current = connection;
        }

        await connection.start();

        if (!isMounted) return;

        console.log('[LiveScores] connected', connection.connectionId);

        await connection.invoke('JoinEventGroup', eventId);

        console.log('[LiveScores] joined group', `event-${eventId}`);
      } catch (err) {
        console.warn('[LiveScores] connection failed', err);

        clearRetryTimer();

        retryTimerRef.current = window.setTimeout(() => {
          void startConnection();
        }, 3000);
      } finally {
        isStartingRef.current = false;
      }
    };

    const forceRestartConnection = async () => {
      if (!navigator.onLine) return;

      const existingConnection = connectionRef.current;

      try {
        if (existingConnection) {
          existingConnection.off('ScoreSaved');
          existingConnection.off('ScoreDeleted');
          existingConnection.off('JudgeStatusChanged');
          existingConnection.off('JudgePortalDataChanged');
          existingConnection.off('JudgeHelpMessageSent');

          await existingConnection.stop().catch(() => undefined);
        }
      } finally {
        connectionRef.current = null;
        void startConnection();
      }
    };

    const handleOnline = () => {
      console.log('[LiveScores] browser online — forcing reconnect');
      void forceRestartConnection();
    };

    const handleFocusOrVisible = () => {
      if (document.visibilityState === 'visible') {
        void startConnection();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocusOrVisible);
    document.addEventListener('visibilitychange', handleFocusOrVisible);

    void startConnection();

    return () => {
      isMounted = false;
      clearRetryTimer();

      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocusOrVisible);
      document.removeEventListener('visibilitychange', handleFocusOrVisible);

      const connection = connectionRef.current;
      connectionRef.current = null;

      if (connection) {
        connection.off('ScoreSaved');
        connection.off('JudgeStatusChanged');
        connection.off('JudgePortalDataChanged');

        void connection
          .invoke('LeaveEventGroup', eventId)
          .catch(() => undefined)
          .finally(() => {
            void connection.stop();
          });
      }
    };
  }, [eventId, enabled]);
}